import { useCallback, useEffect, useRef } from 'react'
import zhLocale from '@fullcalendar/core/locales/zh-cn'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin, { Draggable } from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import './publishing-calendar.css'

export interface PublishingCalendarEvent {
  id: string
  title: string
  start: string
  platform: string
  status: string
  editable?: boolean
  /** `attention` marks an event that is waiting on the reader for something. */
  tone?: 'attention'
}
const DRAG_ATTR = 'data-calendar-drag'
/* The grid shows the hours that carry something, not all 24: a day whose rows
   all fit is a day nobody has to scroll to read. The default window is the one
   publishing actually happens in; an event outside it widens the window rather
   than hiding under a scrollbar. */
const DEFAULT_FROM = 8
const DEFAULT_TO = 20
function hourWindow(events: PublishingCalendarEvent[]) {
  let from = DEFAULT_FROM
  let to = DEFAULT_TO
  for (const e of events) {
    const hour = new Date(e.start).getHours()
    if (hour < from) from = hour
    if (hour + 1 > to) to = hour + 1
  }
  return { from: Math.max(0, from), to: Math.min(24, to) }
}

/**
 * Ref for a container whose `data-calendar-drag` children can be dragged onto a
 * `PublishingTimeCalendar`. The attribute's value is the id handed to `onDrop`;
 * nothing is added to the calendar, so the caller persists and refetches.
 */
export function useCalendarDragSource(enabled = true) {
  return useCallback(
    (el: HTMLElement | null) => {
      if (!enabled || !el) return
      const draggable = new Draggable(el, {
        itemSelector: `[${DRAG_ATTR}]`,
        /* A real preview event is what shows the slot the drop will land on;
           `eventReceive` takes it back out again once the caller has the date. */
        eventData: (item) => ({ title: item.querySelector('strong')?.textContent ?? '' }),
      })
      return () => draggable.destroy()
    },
    [enabled]
  )
}

/** Time-based calendar for publication instants. Event height is for readability, not a publishing duration. */
export function PublishingTimeCalendar({
  date,
  view,
  locale,
  events,
  onOpen,
  onSelect,
  label,
  onMove,
  onDrop,
}: {
  date: Date
  view: 'day' | 'week' | 'month'
  locale: string
  events: PublishingCalendarEvent[]
  onOpen: (id: string) => void
  onSelect?: (date: Date, allDay: boolean) => void
  label: string
  onMove?: (id: string, date: Date) => Promise<void>
  onDrop?: (id: string, date: Date) => void
}) {
  const ref = useRef<FullCalendar>(null)
  const { from, to } = hourWindow(events)
  useEffect(() => {
    ref.current
      ?.getApi()
      .changeView(
        view === 'month' ? 'dayGridMonth' : view === 'day' ? 'timeGridDay' : 'timeGridWeek',
        date
      )
  }, [date, view])
  return (
    <section
      className={`publication-time-calendar publication-time-calendar--${view}`}
      aria-label={label}
    >
      <FullCalendar
        ref={ref}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={
          view === 'month' ? 'dayGridMonth' : view === 'day' ? 'timeGridDay' : 'timeGridWeek'
        }
        initialDate={date}
        locale={locale === 'zh' ? zhLocale : 'en-gb'}
        firstDay={1}
        headerToolbar={false}
        height='100%'
        allDaySlot={false}
        nowIndicator
        expandRows
        slotMinTime={`${String(from).padStart(2, '0')}:00:00`}
        slotMaxTime={`${String(to).padStart(2, '0')}:00:00`}
        slotDuration='01:00:00'
        /* Snapping by the slot made the drag mirror jump a whole hour row at a
           time, which reads as a teleport rather than a drag. */
        snapDuration='00:15:00'
        eventStartEditable={!!onMove}
        droppable={!!onDrop}
        drop={(info) => {
          const id = info.draggedEl.getAttribute(DRAG_ATTR)
          if (id) onDrop?.(id, info.date)
        }}
        eventReceive={(info) => info.revert()}
        eventDurationEditable={false}
        eventDrop={async (info) => {
          try {
            if (!info.event.start || !onMove) return info.revert()
            await onMove(info.event.id, info.event.start)
          } catch {
            info.revert()
          }
        }}
        slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
        eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
        dayHeaderFormat={
          view === 'month'
            ? { weekday: 'short' }
            : { weekday: 'short', month: 'numeric', day: 'numeric' }
        }
        dayHeaderContent={(info) => (
          <span className='publication-time-day-heading'>
            <span>{info.date.toLocaleDateString(locale, { weekday: 'long' })}</span>
            {view !== 'month' && (
              <strong>
                {info.date.toLocaleDateString(locale, { month: '2-digit', day: '2-digit' })}
              </strong>
            )}
          </span>
        )}
        displayEventEnd={false}
        defaultTimedEventDuration='01:00:00'
        slotEventOverlap={false}
        eventMaxStack={view === 'day' ? 3 : 1}
        dayMaxEvents={3}
        fixedWeekCount={false}
        eventInteractive
        events={events.map((e) => ({
          id: e.id,
          title: e.title,
          start: e.start,
          startEditable: !!onMove && e.editable !== false,
          extendedProps: { platform: e.platform, status: e.status, tone: e.tone },
        }))}
        eventClick={(info) => onOpen(info.event.id)}
        dateClick={(info) => onSelect?.(info.date, info.allDay)}
        selectable={!!onSelect}
        selectMirror
        select={(info) => {
          onSelect?.(info.start, info.allDay)
          ref.current?.getApi().unselect()
        }}
        eventContent={(info) => (
          <div className='publication-time-event' data-tone={info.event.extendedProps.tone}>
            {/* Time, platform and state on one line: a compact grid gives an
                hour about two lines, and a title is worth more than a third. */}
            <div className='publication-time-event-meta'>
              <time>{info.timeText}</time>
              <span>{info.event.extendedProps.platform}</span>
              <span className='publication-time-event-status'>
                {info.event.extendedProps.status}
              </span>
            </div>
            <strong>{info.event.title}</strong>
          </div>
        )}
      />
    </section>
  )
}
